import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useLegacyAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

export const CreateDebtorCommentSchema = z.object({
  id: z.union([z.string(), z.number()]),
  comment: z.string(),
});

export type CreateDebtorCommentVariables = z.infer<typeof CreateDebtorCommentSchema>;

export async function createDebtorCommentFn(
  axiosInstance: Promise<AxiosInstance>,
  data: CreateDebtorCommentVariables,
) {
  const instance = await axiosInstance;

  return instance.post(`debtors/${data.id}/comments`, CreateDebtorCommentSchema.parse(data));
}

export function useCreateDebtorComment() {
  const client = useQueryClient();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, CreateDebtorCommentVariables>({
    mutationFn: data => createDebtorCommentFn(axiosInstance, data),
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['comments'],
      });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    CreateDebtorCommentVariables,
    'createDebtorComment'
  >('createDebtorComment', mutation);
}
